import { Injectable } from '@angular/core'
import { Router, NavigationExtras, NavigationEnd } from '@angular/router'
import { LogService, Project, ProjectCMSRouterPageType } from 'htv-lib'
import { filter } from 'rxjs'

export enum RouterPage {
  APP_GRID = 'APP_GRID',
  CAST = 'CAST',
  CAST_DONGLE = 'CAST_DONGLE',
  CHANNEL = 'CHANNEL',
  CHANNEL_GRID = 'CHANNEL_GRID',
  CHANNEL_IPG = 'CHANNEL_IPG',
  EXTERNAL_LINK = 'EXTERNAL_LINK',
  HDMI = 'HDMI',
  HDMI_NOT_FOUND = 'HDMI_NOT_FOUND',
  LANGUAGE_SELECTOR = 'LANGUAGE_SELECTOR',
  VOD = 'VOD',
  VOD_CATALOG = 'VOD_CATALOG',
  WAKE_UP_ALARM = 'WAKE_UP_ALARM',
  WAKE_UP_ALARM_RINGING = 'WAKE_UP_ALARM_RINGING',
  WELCOME = 'WELCOME',
}

interface RouterRoute {
  type: RouterPage
  usePageId: boolean
  pageId?: string
  url: string
}

const defaultRoutes: { [key in RouterPage]: string } = {
  [RouterPage.APP_GRID]: '/apps',
  [RouterPage.CAST]: '/cast',
  [RouterPage.CAST_DONGLE]: '/cast-dongle',
  [RouterPage.CHANNEL]: '/channel',
  [RouterPage.CHANNEL_GRID]: '/channel-grid',
  [RouterPage.CHANNEL_IPG]: '/program-guide',
  [RouterPage.EXTERNAL_LINK]: '/external-link',
  [RouterPage.HDMI]: '/hdmi',
  [RouterPage.HDMI_NOT_FOUND]: '/hdmi-not-found',
  [RouterPage.LANGUAGE_SELECTOR]: '/language',
  [RouterPage.VOD]: '/vod',
  [RouterPage.VOD_CATALOG]: '/vod-catalog',
  [RouterPage.WAKE_UP_ALARM]: '/wake-up-alarm',
  [RouterPage.WAKE_UP_ALARM_RINGING]: '/alarm-ringing',
  [RouterPage.WELCOME]: '/home',
}

type Routes = {
  [key in RouterPage]?: RouterRoute
}

/*
 * Resources
 * https://procentric.developer.lge.com/docs/smart-app-development/back-button
 */
@Injectable({
  providedIn: 'root',
})
export class RouterService {
  routes: Routes = {}
  routeHistory: string[] = []
  startPageId?: string

  constructor(
    private logService: LogService,
    private router: Router
  ) {
    // do nothing
  }

  init(): void {
    this.logService.info(`[RouterService] Init`)
    this.routes = this.buildDefaultRoutes()
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(e => {
      const event = e as NavigationEnd
      this.routeHistory.push(event.urlAfterRedirects)
      if (this.routeHistory.length > 10) {
        this.routeHistory.shift()
      }
    })
  }

  initFromProject(project: Project): void {
    this.logService.debug(`[RouterService] Init with project data`)
    const routes = this.buildDefaultRoutes()

    const { pageConfig } = project.configuration
    if (pageConfig) {
      pageConfig.forEach(pageConfig => {
        const routerPage = this.getRouterPageFromProjectCMSRouterPageType(pageConfig.type)
        if (routerPage) {
          if (routes[routerPage]) {
            routes[routerPage] = {
              type: routerPage,
              usePageId: true,
              pageId: pageConfig.pageId,
              url: `/page/${pageConfig.pageId}`,
            }
            this.logService.info(`[RouterService] Created custom route ${routerPage} -> ${pageConfig.pageId}`)
          } else {
            this.logService.error(`[RouterService] Could not find route for page config with type ${pageConfig.type}`)
          }
        } else {
          this.logService.error(`[RouterService] Invalid route type ${pageConfig.type}`)
        }
      })
    } else {
      this.logService.debug(`[RouterService] No pages configuration in Project`)
    }

    this.routes = routes
    this.startPageId = project.cms.startPageId
  }

  private buildDefaultRoutes(): Routes {
    const routes: Routes = {}
    const keys = Object.keys(RouterPage) as Array<keyof typeof RouterPage>
    keys.forEach(key => {
      const type = RouterPage[key]
      routes[type] = {
        type: type,
        usePageId: false,
        url: defaultRoutes[type],
      }
    })
    return routes
  }

  getRouterPageFromProjectCMSRouterPageType(type: ProjectCMSRouterPageType): RouterPage | undefined {
    switch (type) {
      case ProjectCMSRouterPageType.APP_GRID:
        return RouterPage.APP_GRID
      case ProjectCMSRouterPageType.CAST:
        return RouterPage.CAST
      case ProjectCMSRouterPageType.CAST_DONGLE:
        return RouterPage.CAST_DONGLE
      case ProjectCMSRouterPageType.CHANNEL:
        return RouterPage.CHANNEL
      case ProjectCMSRouterPageType.CHANNEL_GRID:
        return RouterPage.CHANNEL_GRID
      case ProjectCMSRouterPageType.CHANNEL_IPG:
        return RouterPage.CHANNEL_IPG
      case ProjectCMSRouterPageType.EXTERNAL_LINK:
        return RouterPage.EXTERNAL_LINK
      case ProjectCMSRouterPageType.LANGUAGE_SELECTOR:
        return RouterPage.LANGUAGE_SELECTOR
      case ProjectCMSRouterPageType.WAKE_UP_ALARM:
        return RouterPage.WAKE_UP_ALARM
      case ProjectCMSRouterPageType.WAKE_UP_ALARM_RINGING:
        return RouterPage.WAKE_UP_ALARM_RINGING
      default:
        return undefined
    }
  }

  private _reload(url: string): void {
    const shouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute
    const onSameUrlNavigation = this.router.onSameUrlNavigation

    this.router.routeReuseStrategy.shouldReuseRoute = () => false
    this.router.onSameUrlNavigation = 'reload'

    this.router.navigateByUrl(url).finally(() => {
      this.router.routeReuseStrategy.shouldReuseRoute = shouldReuseRoute
      this.router.onSameUrlNavigation = onSameUrlNavigation
    })
  }

  reload(): void {
    this.logService.info(`[RouterService] Reloading page...`)
    this._reload(this.router.url)
  }

  reloadToHome(): void {
    this.logService.info(`[RouterService] Reloading to home page...`)
    this._reload('/')
  }

  reloadToWelcome(): void {
    this.logService.info(`[RouterService] Reloading to welcome page...`)
    const urlFragments = this.getRouterPageUrlFragments(RouterPage.WELCOME)
    if (urlFragments && urlFragments.length > 0) {
      const url = this.router.serializeUrl(this.router.createUrlTree(urlFragments))
      this._reload(url)
    }
  }

  goBack(): void {
    if (this.isStartPage()) {
      this.routeHistory = ['/']
      return
    }
    if (this.routeHistory.length > 1) {
      this.routeHistory.pop()
      const previousUrl = this.routeHistory[this.routeHistory.length - 1]
      this.router.navigateByUrl(previousUrl)
      this.routeHistory.pop()
    }
  }

  private getRouterPageUrlFragments(type: RouterPage): string[] | null {
    const route = this.routes[type]
    if (route) {
      if (route.usePageId) {
        if (route.pageId) {
          return ['page', route.pageId]
        } else {
          this.logService.error(`[RouterService] Could not find route pageId for ${type}`)
        }
      } else {
        return [route.url]
      }
    } else {
      this.logService.error(`[RouterService] Could not find route for ${type}`)
    }
    return null
  }

  async goToPage(type: RouterPage, extras?: NavigationExtras): Promise<void> {
    this.logService.info(`[RouterService] Go to page ${type}`)
    const urlFragments = this.getRouterPageUrlFragments(type)
    if (urlFragments && urlFragments.length > 0) {
      await this.router.navigate(urlFragments, extras)
    }
  }

  goToProjectPage(pageId: string): void {
    this.logService.info(`[RouterService] Go to project page ${pageId}`)
    this.router.navigate(['page', pageId])
  }

  goToHome(): void {
    this.logService.info(`[RouterService] Go to home page`)
    this.router.navigate(['/'])
  }

  goToRegisterPage(): void {
    this.logService.info(`[RouterService] Go to register page`)
    this.router.navigate(['/register'])
  }

  goToTestPage(): void {
    this.logService.info(`[RouterService] Go to test page`)
    this.router.navigate(['/test'])
  }

  restartApp(): void {
    this.logService.info(`[RouterService] Restarting app...`)
    window.location.href = 'index.html'
  }

  isCurrentPage(type: RouterPage): boolean {
    const url = this.routes[type]?.url
    if (url) {
      return this.router.url == url
    } else {
      return false
    }
  }

  isCurrentPageUrl(url: string): boolean {
    return this.router.url == url
  }

  isStartPage(): boolean {
    if (this.isCurrentPageUrl('/')) {
      return true
    }
    if (this.startPageId) {
      const startPageUrl = `/page/${this.startPageId}`
      return this.isCurrentPageUrl(startPageUrl)
    }
    return false
  }
}
